import { useSyncExternalStore } from "react";

/**
 * Whether the support chat is open (client 2026-10-08). The drawer's Help and
 * Support menu opens it; SupportCenter, mounted once at the app root, shows it
 * over whatever page is open. (The FAQ and Contact Us are pages of each app —
 * client 2026-10-12.)
 */
export type SupportView = "chat";

let view: SupportView | null = null;
const listeners = new Set<() => void>();
const setView = (next: SupportView | null) => {
  if (view === next) return;
  view = next;
  listeners.forEach((listener) => listener());
};

/**
 * Back closes the chat, and only the chat (client 2026-10-09): opening it
 * adds a history entry — the same page, marked — so Back pops that, not the
 * app's page under it. Closing it any other way takes the entry back off.
 */
const MARK = "mofoSupport";
let pushed = false;
const onMarkedEntry = () =>
  (window.history.state as Record<string, unknown> | null)?.[MARK] === true;

export const openSupport = (next: SupportView): void => {
  if (view === null && !pushed && typeof window !== "undefined") {
    // The router's own state is kept, so it sees the same page.
    window.history.pushState({ ...(window.history.state ?? {}), [MARK]: true }, "");
    pushed = true;
  }
  setView(next);
};
export const closeSupport = (): void => {
  // Still on the entry opening added (closed with X, not Back): take it off.
  const takeBack = pushed && typeof window !== "undefined" && onMarkedEntry();
  pushed = false;
  setView(null);
  if (takeBack) window.history.back();
};

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

export const useSupportView = (): SupportView | null =>
  useSyncExternalStore(
    subscribe,
    () => view,
    () => null
  );
